/**
 * Note / activity-log action handlers extracted verbatim from
 * `ContactEditModal.tsx` and parameterized on a typed deps object.
 *
 * The orchestrator keeps the (non-memoized) handler shells and delegates here,
 * so behavior is unchanged (#3293 Pass-2). These cover the activity-log
 * lifecycle: add, delete, edit-start, save-edit, cancel-edit. Each persists to
 * the backend in BOTH the canonical notes format and the legacy activities
 * format via `onSave`, but only for EXISTING contacts (`!isNew && contact`).
 */

import type { MutableRefObject } from 'react';
import type { Activity, ContactObject } from './types';
import { activitiesToNotes } from './contactMapping';

interface NoteAuthorProfile {
  id?: string;
  name?: string;
}

export interface ContactNotesDeps {
  activities: Activity[];
  setActivities: (a: Activity[]) => void;
  newNote: string;
  setNewNote: (s: string) => void;
  noteType: 'note' | 'call' | 'email' | 'meet';
  setNoteType: (t: 'note' | 'call' | 'email' | 'meet') => void;
  editingNoteId: string | null;
  setEditingNoteId: (id: string | null) => void;
  editingNoteText: string;
  setEditingNoteText: (s: string) => void;
  isEditingNoteRef: MutableRefObject<boolean>;
  profile: NoteAuthorProfile | null | undefined;
  isNew: boolean | undefined;
  contact: ContactObject | null | undefined;
  onSave: (data: Partial<ContactObject>) => void;
}

export function handleAddNote(deps: ContactNotesDeps): void {
  const { activities, setActivities, newNote, setNewNote, noteType, setNoteType, profile, isNew, contact, onSave } = deps;
  const trimmed = newNote.trim();
  if (!trimmed) return;

  const newActivity: Activity = {
    id: Date.now().toString(),
    date: new Date().toISOString(),
    note: trimmed,
    authorId: profile?.id || '',
    authorName: profile?.name || 'Unknown',
    type: noteType,
  };

  const updatedActivities = [newActivity, ...activities];
  setActivities(updatedActivities);
  setNewNote('');
  setNoteType('email');
  // Immediately save to backend in both formats
  if (!isNew && contact) {
    onSave({ activities: updatedActivities, notes: activitiesToNotes(updatedActivities) } as Partial<ContactObject>);
  }
}

export function handleDeleteNote(id: string, deps: ContactNotesDeps): void {
  const { activities, setActivities, isNew, contact, onSave } = deps;
  const updatedActivities = activities.filter(a => a.id !== id);
  setActivities(updatedActivities);
  if (!isNew && contact) {
    onSave({
      activities: updatedActivities.length > 0 ? updatedActivities : undefined,
      notes: updatedActivities.length > 0 ? activitiesToNotes(updatedActivities) : undefined,
    } as Partial<ContactObject>);
  }
}

export function handleEditNote(activity: Activity, deps: ContactNotesDeps): void {
  const { setEditingNoteId, setEditingNoteText, isEditingNoteRef } = deps;
  setEditingNoteId(activity.id);
  setEditingNoteText(activity.note);
  isEditingNoteRef.current = true;
}

export function handleSaveNote(deps: ContactNotesDeps): void {
  const { activities, setActivities, editingNoteId, setEditingNoteId, editingNoteText, setEditingNoteText, isEditingNoteRef, isNew, contact, onSave } = deps;
  if (!editingNoteId) return;
  const trimmed = editingNoteText.trim();
  if (!trimmed) {
    handleDeleteNote(editingNoteId, deps);
  } else {
    const updatedActivities = activities.map(a =>
      a.id === editingNoteId ? { ...a, note: trimmed } : a
    );
    setActivities(updatedActivities);
    if (!isNew && contact) {
      onSave({ activities: updatedActivities, notes: activitiesToNotes(updatedActivities) } as Partial<ContactObject>);
    }
  }
  setEditingNoteId(null);
  setEditingNoteText('');
  // Delay resetting the ref to allow overlay click handler to check it first
  setTimeout(() => {
    isEditingNoteRef.current = false;
  }, 100);
}

export function handleCancelEdit(deps: ContactNotesDeps): void {
  const { setEditingNoteId, setEditingNoteText, isEditingNoteRef } = deps;
  setEditingNoteId(null);
  setEditingNoteText('');
  // Delay resetting the ref to allow overlay click handler to check it first
  setTimeout(() => {
    isEditingNoteRef.current = false;
  }, 100);
}
